import { X } from "lucide-react";
import type { ReactNode } from "react";
import { cn } from "@/lib/utils";

type ChipTone = "default" | "good" | "danger";

const TONES: Record<ChipTone, string> = {
  default: "border-input bg-muted/40 text-foreground",
  good: "border-emerald-500/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300",
  danger:
    "border-destructive/40 bg-destructive/10 text-destructive dark:text-destructive-foreground",
};

/**
 * A small pill used for filter facets and "why matched" signals. `good` is the
 * positive (must-have / strength) tone, `danger` flags gaps, and an optional
 * remove button turns it into an editable token.
 */
export function Chip({
  children,
  tone = "default",
  onRemove,
}: {
  children: ReactNode;
  tone?: ChipTone;
  onRemove?: () => void;
}) {
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 rounded-full border px-2.5 py-[3px] text-xs font-medium leading-none",
        TONES[tone],
      )}
    >
      {tone === "good" && (
        <span
          className="mr-px inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-emerald-500"
          aria-hidden
        />
      )}
      {children}
      {onRemove && (
        <button
          type="button"
          onClick={onRemove}
          className="-mr-0.5 ml-0.5 grid h-3.5 w-3.5 place-items-center rounded-full opacity-60 transition hover:bg-foreground/10 hover:opacity-100"
          aria-label="Remover"
        >
          <X size={11} strokeWidth={2.5} />
        </button>
      )}
    </span>
  );
}
